import React, { useEffect, useRef } from "react";
import * as THREE from "three";

const Cube = ({ onFaceClick }) => {
  const mountRef = useRef(null);

  useEffect(() => {
    const currentMount = mountRef.current;
    const width = currentMount.clientWidth;
    const height = currentMount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.z = 6.5;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    currentMount.appendChild(renderer.domElement);

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    // Variables para el control de rotación manual
    let isDragging = false;
    let previousMousePosition = { x: 0, y: 0 };
    let dragThreshold = false; // Para diferenciar clic de arrastre

    const createTextTexture = (text) => {
      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "rgba(0, 20, 40, 0.85)";
      ctx.fillRect(0, 0, 512, 512);
      ctx.strokeStyle = "#00ffff";
      ctx.lineWidth = 15;
      ctx.strokeRect(0, 0, 512, 512);
      ctx.fillStyle = "#00ffff";
      ctx.font = "Bold 55px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, 256, 256);
      const texture = new THREE.CanvasTexture(canvas);
      return new THREE.MeshBasicMaterial({ map: texture, transparent: true });
    };

    const faceLabels = ["WEB3", "FULLSTACK", "AI", "APP DESIGN", "ORACLES", "SC SECURITY"];
    const materials = faceLabels.map(label => createTextTexture(label));
    const geometry = new THREE.BoxGeometry(3.5, 3.5, 3.5);
    const cube = new THREE.Mesh(geometry, materials);
    scene.add(cube);

    // Animación principal
    const animate = () => {
      requestAnimationFrame(animate);
      
      // Rotación automática suave (solo si NO se está arrastrando)
      if (!isDragging) {
        cube.rotation.x += 0.003;
        cube.rotation.y += 0.005;
      }
      
      renderer.render(scene, camera);
    };
    animate();

    // --- LÓGICA DE INTERACCIÓN CON EL MOUSE ---

    const handleMouseDown = (e) => {
      isDragging = true;
      dragThreshold = false; // Reset al iniciar
      previousMousePosition = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;

      const deltaMove = {
        x: e.clientX - previousMousePosition.x,
        y: e.clientY - previousMousePosition.y
      };

      // Si el movimiento es mayor a 5 pixeles, lo consideramos arrastre, no clic
      if (Math.abs(deltaMove.x) > 5 || Math.abs(deltaMove.y) > 5) {
        dragThreshold = true; 
      }

      // Aplicar rotación basada en el movimiento del mouse
      const sensitivity = 0.005;
      cube.rotation.y += deltaMove.x * sensitivity;
      cube.rotation.x += deltaMove.y * sensitivity;

      previousMousePosition = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = (e) => {
      isDragging = false;

      // SI NO HUBO ARRASTRE (fue un clic rápido), disparamos el Raycaster para el Modal
      if (!dragThreshold) {
        const rect = renderer.domElement.getBoundingClientRect();
        mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

        raycaster.setFromCamera(mouse, camera);
        const intersects = raycaster.intersectObject(cube);

        if (intersects.length > 0) {
          const index = intersects[0].face.materialIndex;
          onFaceClick(faceLabels[index]);
        }
      }
    };

    // Agregar listeners al elemento del renderer
    const canvas = renderer.domElement;
    canvas.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove); // Window para no perder el foco
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      canvas.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      currentMount.removeChild(canvas);
      renderer.dispose();
    };
  }, [onFaceClick]);

  return <div ref={mountRef} style={{ width: "100%", height: "100%", cursor: "grab" }} />;
};

export default Cube;